/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Page History Inquires component
 *
 * @author Vladislav Oshkanov
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import { Fragment, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { TabItem } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { setHelpTipTextAction } from '@/main/components/Header/headerActions';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@/main/modules/logging';
import { ONBOARDING_DATA_ATTR } from '@/main/modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@/main/modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@/main/modules/role/selectors/guardSelector';
import { InstructionLink } from '@components/common';
import { ELinkType } from '@components/common/InstructionLink/types';
import PageTitle, { renderDropFilterButton } from '@components/common/PageTitle/PageTitle';
import Title from '@components/common/Title/Title';
import InquiryState from '@modules/inquiryState/components/InquiryState';
import { inquiryOtherStateIsComplete, inquiryStateIsComplete } from '@modules/inquiryState/selectors';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectPageHistoryInquiryTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import {
    initialise,
    Table,
    TableDensity,
    TableExport,
    useTableFilterApi,
    useTableInitialise,
    useTableListApi,
    useTableMetaApi,
    useTableResizeApi,
} from '@modules/table';
import {
    tableIsErrorSelector,
    tableIsLoadedWithDataSelector,
    tableIsLoadingSelector,
    tableMetaFilterSelector,
} from '@modules/table/selectors';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { TableInquiryColumns } from './components/TableInquiryColumns';
import { TableOtherInquiryColumns } from './components/TableOtherInquiryColumns';
import {
    HISTORY_INQUIRY_EXPORT_URL,
    HISTORY_INQUIRY_TABLE_CONFIG,
    OTHER_INQUIRY_EXPORT_URL,
    OTHER_INQUIRY_TABLE_CONFIG,
} from './constants';
import { initHistoryInquiryOnboarding } from './pageHistoryInquiryActions';
import { useSendInquiryListViewedMutation } from './store';
import {
    InquiriesInstructionsLinksStyled,
    InquiriesTabMenuContainerStyled,
    TabMenuInquiryTableStyled,
} from './styled/PageHistoryInquiryStyled';
import { EInquiryTableType, ITableDefaultInquiry, ITableOtherInquiry } from './types';

const TABLE_COLUMNS: Array<keyof ITableDefaultInquiry> = [
    'account',
    'closeDate',
    'createDate',
    'description',
    'id',
    'subscriberNumber',
    'topic',
    'status',
    'updateDate',
];

const TABLE_OTHER_COLUMNS: Array<keyof ITableOtherInquiry> = [
    'account',
    'closeDate',
    'createDate',
    'id',
    'subscriberNumber',
    'topic',
    'status',
    'updateDate',
    'processingReport',
];

export const PageHistoryInquiry = () => {
    const dispatch = useDispatch();

    const commonTranslation = useSelector(makeSelectTranslationCommon());
    const pageTranslation = useSelector(makeSelectPageHistoryInquiryTranslation());
    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));
    const isStateComplete = useSelector(inquiryStateIsComplete);
    const isOtherStateComplete = useSelector(inquiryOtherStateIsComplete);
    const isOtherInquiryFeatureEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.otherInquiries),
    );

    const { instructionsLinks: { inqsForms, inqsHowCreate, inqsGeneral, inqsExport } } = commonTranslation

    const [currentTableType, setCurrentTableType] = useState<EInquiryTableType>(EInquiryTableType.DEFAULT);

    const isDefaultInquiryTableTab = currentTableType === EInquiryTableType.DEFAULT;
    const isOtherInquiryTableTab = currentTableType === EInquiryTableType.OTHER;


    const [sendInquiryListViewed] = useSendInquiryListViewedMutation();

    /* Table Meta  */
    const config = useMemo(() => TableInquiryColumns(pageTranslation), [pageTranslation]);
    const configOther = useMemo(() => TableOtherInquiryColumns(pageTranslation), [pageTranslation]);

    const { columns } = useTableMetaApi(config);
    const { columns: columnsOther } = useTableMetaApi(configOther);

    /* Table List */
    const { data, count, isCompact, infiniteLoader } = useTableListApi();

    /* Table Filter */
    const { drop } = useTableFilterApi();

    const defaultTableFilterSelector = useMemo(() => tableMetaFilterSelector('inquiry/meta', TABLE_COLUMNS), []);
    const selectedDefaultTableFilters = useSelector(defaultTableFilterSelector);

    const otherTableFilterSelector = useMemo(
        () => tableMetaFilterSelector('other-inquiry/meta', TABLE_OTHER_COLUMNS),
        [],
    );
    const selectedOtherTableFilters = useSelector(otherTableFilterSelector);

    /* Table Resize */
    const { resize } = useTableResizeApi();

    /* Table Statuses  */
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);
    const isLoadedWithData = useSelector(tableIsLoadedWithDataSelector);

    const handleTabChange = useCallback((event: SyntheticEvent, value) => {
        event.stopPropagation();
        setCurrentTableType(value);
    }, []);

    const onOtherInquiriesClickLogBusinessEvent = () => {
        if (!isOtherInquiryTableTab) {
            dispatch(
                logBusinessEvent({
                    eventName: ELogBusinessEventName.USER_SHOW_OTHER_INQUIRIES,
                    type: ELogBusinessEventType.common,
                }),
            );
        }
    };

    const { updatedConfig } = useTableInitialise(HISTORY_INQUIRY_TABLE_CONFIG);
    const { updatedConfig: updatedConfigOther } = useTableInitialise(OTHER_INQUIRY_TABLE_CONFIG);

    useEffect(() => {
        if (isLoadedWithData) sendInquiryListViewed();
    }, [isLoadedWithData, sendInquiryListViewed]);

    useEffect(() => {
        dispatch(initHistoryInquiryOnboarding());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Инициализация таблицы */
    useEffect(() => {
        if (isDefaultInquiryTableTab) {
            if (isStateComplete && updatedConfig) {
                dispatch(initialise(updatedConfig));
            }
        } else {
            if (isOtherStateComplete && updatedConfigOther) {
                dispatch(initialise(updatedConfigOther));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStateComplete, isOtherStateComplete, updatedConfig, updatedConfigOther, currentTableType]);

    useEffect(() => {
        if (isOtherInquiryTableTab) {
            dispatch(setHelpTipTextAction(commonTranslation.contextHelp.inquiriesOther));
        } else dispatch(setHelpTipTextAction(''));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTableType]);

    return (
        <Fragment>
            <PageLocale lazy fileNames={[
                TranslationFileNamesEnum.pageHistoryInquiry,
                TranslationFileNamesEnum.common,
            ]} />
            <Title pageName={ECommonTranslationPages.inquiry} />
            <PageTitle
                count={count}
                dataName="historyInquiry"
                instructionLink={inqsGeneral}
                title={pageTranslation.title.label}
            >
                {
                    // render drop filter button
                    !!Object.keys(isDefaultInquiryTableTab ? selectedDefaultTableFilters : selectedOtherTableFilters)
                        .length &&
                        renderDropFilterButton({
                            onClick: drop,
                            value: pageTranslation.title.dropFilters,
                        })
                }
                <InquiryState inquiryTableType={currentTableType} />
                <TableExport
                    instructionLink={inqsExport}
                    url={isDefaultInquiryTableTab ? HISTORY_INQUIRY_EXPORT_URL : OTHER_INQUIRY_EXPORT_URL}
                />
                <TableDensity />
            </PageTitle>

            <InquiriesTabMenuContainerStyled>
                {isOtherInquiryFeatureEnabled && (
                    <TabMenuInquiryTableStyled
                        autoWidth
                        value={currentTableType}
                        onChange={handleTabChange}
                        {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.inquiriesTabMenu }}
                    >
                        <TabItem
                            label={pageTranslation.tabMenu[EInquiryTableType.DEFAULT]}
                            value={EInquiryTableType.DEFAULT}
                        />
                        <TabItem
                            label={pageTranslation.tabMenu[EInquiryTableType.OTHER]}
                            value={EInquiryTableType.OTHER}
                            onClick={onOtherInquiriesClickLogBusinessEvent}
                        />
                    </TabMenuInquiryTableStyled>
                )}
                {isInstructionsLinksEnabled &&
                    <InquiriesInstructionsLinksStyled>
                        {inqsHowCreate && inqsHowCreate.instructionLink &&
                            <InstructionLink {...inqsHowCreate} linkType={ELinkType.TEXT} />
                        }
                        {inqsForms && inqsForms.instructionLink &&
                            <InstructionLink {...inqsForms} linkType={ELinkType.TEXT} />
                        }
                    </InquiriesInstructionsLinksStyled>}
            </InquiriesTabMenuContainerStyled>


            <Table
                columns={isDefaultInquiryTableTab ? columns : columnsOther}
                data={data}
                dataName={isDefaultInquiryTableTab ? 'historyInquiry' : 'otherInquiry'}
                dataNameRow="account.id"
                infiniteScroll={infiniteLoader}
                isCompact={isCompact}
                isError={isError}
                isInnerHtmlAvailable={true}
                isLoading={isLoading}
                onResize={resize}
            />
        </Fragment>
    );
};

export default PageHistoryInquiry;
